import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Coins, Trash2, CheckCircle2, Crown, Sparkles, AlertCircle, Loader2, Download } from "lucide-react";

function formatPounds(pence) {
  if (!pence) return "£0.00";
  return `£${(pence / 100).toFixed(2)}`;
}

function shortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

// RFC-4180 CSV cell escaping — wrap in quotes if the value contains a
// comma / quote / newline; double any embedded quotes. Numbers / booleans
// coerced to strings first. `null` / `undefined` render as an empty cell.
function csvCell(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(rows, headers) {
  const headerLine = headers.map((h) => csvCell(h.label)).join(",");
  const bodyLines = rows.map((r) => headers.map((h) => csvCell(h.value(r))).join(","));
  return [headerLine, ...bodyLines].join("\r\n");
}

// Derive the pub slug from the browser URL (`/c/<slug>/editor`). Used to
// stamp the CSV filename so each pub's export is instantly identifiable
// in a downloads folder. Falls back to "master" on the global surface.
function currentPubSlug() {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/c\/([^/]+)(?:\/|$)/);
  return m ? m[1] : null;
}

export default function ManualPayoutsCard() {
  const [closures, setClosures] = useState([]); // every closed season
  const [payouts, setPayouts] = useState([]);   // every payout row across all seasons
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [notesDraft, setNotesDraft] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/admin/season-closures"),
        api.get("/admin/payouts"),
      ]);
      const closureRows = Array.isArray(c.data) ? c.data : [];
      setClosures(closureRows);
      setPayouts(Array.isArray(p.data) ? p.data : []);
      // Auto-select the most-recent closed season once on first load.
      setSelectedSeasonId((prev) => {
        if (prev) return prev;
        return (closureRows[0]?.season_id) || "";
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seasonPayouts = useMemo(() => {
    if (!selectedSeasonId) return [];
    return payouts
      .filter((r) => r.season_id === selectedSeasonId)
      .sort((a, b) => {
        const ra = a.rank ?? 99;
        const rb = b.rank ?? 99;
        if (ra !== rb) return ra - rb;
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
  }, [payouts, selectedSeasonId]);

  const selectedClosure = useMemo(
    () => closures.find((c) => c.season_id === selectedSeasonId) || null,
    [closures, selectedSeasonId],
  );

  const unpaidAll = useMemo(
    () => payouts
      .filter((r) => !r.paid)
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [payouts],
  );

  const generateForSelected = async () => {
    if (!selectedSeasonId) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/payouts/generate-for-season/${selectedSeasonId}`);
      const count = (data?.payouts || []).length;
      if (data?.already_existed) {
        toast.info(`Payouts were already drafted for ${data.season_name || "this season"}.`);
      } else {
        toast.success(`Drafted ${count} payout${count === 1 ? "" : "s"} for ${data.season_name || "the season"}.`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const togglePaid = async (row) => {
    try {
      await api.patch(`/admin/payouts/${row.id}`, { paid: !row.paid });
      toast.success(row.paid ? "Marked unpaid" : `Marked paid · ${row.winner_name}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Delete payout to ${row.winner_name}?`)) return;
    try {
      await api.delete(`/admin/payouts/${row.id}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onNotesChange = (id, v) => setNotesDraft((s) => ({ ...s, [id]: v }));

  const saveNote = async (row) => {
    const draft = notesDraft[row.id];
    if (draft === undefined) return;
    const next = draft.trim();
    if (next === (row.notes || "").trim()) {
      setNotesDraft((s) => { const { [row.id]: _, ...rest } = s; return rest; });
      return;
    }
    setSavingNoteId(row.id);
    try {
      await api.patch(`/admin/payouts/${row.id}`, { notes: next || "" });
      toast.success("Note saved");
      setNotesDraft((s) => { const { [row.id]: _, ...rest } = s; return rest; });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNoteId(null);
    }
  };

  const totalOwed = payouts.filter((r) => !r.paid).reduce((s, r) => s + (r.amount_pence || 0), 0);
  const totalPaid = payouts.filter((r) => r.paid).reduce((s, r) => s + (r.amount_pence || 0), 0);

  // Download the CURRENT (already slug-scoped) payouts list as a CSV
  // file. `payouts` is populated from `GET /api/admin/payouts`, which
  // the axios interceptor at `/app/frontend/src/lib/api.js` auto-scopes
  // via `?as_pub=<slug>` whenever the editor is on `/c/<slug>/editor`.
  // So this export inherits the same isolation for free — Founders
  // Arms downloads only their own rows, Dugsy master downloads the
  // global roster.
  const downloadCsv = () => {
    if (!payouts.length) {
      toast.info("Nothing to export yet — no payout rows.");
      return;
    }
    const headers = [
      { label: "Season",         value: (r) => r.season_name || "" },
      { label: "Rank",           value: (r) => (r.rank ?? "") },
      { label: "Split %",        value: (r) => (r.split_pct ?? "") },
      { label: "Winner",         value: (r) => r.winner_name || "" },
      { label: "Email",          value: (r) => r.winner_email || "" },
      { label: "Amount (£)",     value: (r) => ((r.amount_pence || 0) / 100).toFixed(2) },
      { label: "Payment method", value: (r) => r.payment_method || "" },
      { label: "Reference",      value: (r) => r.reference || "" },
      { label: "Paid",           value: (r) => (r.paid ? "yes" : "no") },
      { label: "Paid at",        value: (r) => (r.paid_at ? r.paid_at.slice(0, 10) : "") },
      { label: "Match day",      value: (r) => r.match_date || "" },
      { label: "Created",        value: (r) => (r.created_at ? r.created_at.slice(0, 10) : "") },
      { label: "Notes",          value: (r) => r.notes || "" },
    ];
    const csv = toCsv(payouts, headers);
    // Prepend a UTF-8 BOM so Excel opens £ and any accented names cleanly.
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const slug = currentPubSlug() || "master";
    const today = new Date().toISOString().slice(0, 10);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payouts-${slug}-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${payouts.length} row${payouts.length === 1 ? "" : "s"} to CSV.`);
  };

  return (
    <section className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="manual-payouts-card">
      <header className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <Coins className="h-3.5 w-3.5" /> Winner payouts
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">Winner payouts.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Pick a closed season to see its winners (name, email, amount won). Tick a row when you&apos;ve paid it and jot a note for your records.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Button
            type="button"
            onClick={downloadCsv}
            disabled={loading || payouts.length === 0}
            data-testid="payouts-download-csv"
            title={payouts.length === 0 ? "No payouts to export" : "Download the current view as CSV"}
            variant="outline"
            className="h-11 rounded-sm border-zinc-300 text-zinc-900 hover:bg-zinc-100 inline-flex items-center gap-2 px-3"
          >
            <Download className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.14em]">Download CSV</span>
          </Button>
          <div className="px-3 py-2 rounded-sm border border-amber-300 bg-amber-50">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">Owed</p>
            <p className="font-heading text-lg font-bold tabular-nums text-amber-900" data-testid="payouts-total-owed">{formatPounds(totalOwed)}</p>
          </div>
          <div className="px-3 py-2 rounded-sm border border-emerald-300 bg-emerald-50">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">Paid</p>
            <p className="font-heading text-lg font-bold tabular-nums text-emerald-900" data-testid="payouts-total-paid">{formatPounds(totalPaid)}</p>
          </div>
        </div>
      </header>

      {loading ? (
        <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
      ) : closures.length === 0 && payouts.length === 0 ? (
        <div className="text-sm py-10 text-center border border-dashed border-zinc-300 rounded-sm" data-testid="payouts-no-closures">
          <Sparkles className="h-5 w-5 mx-auto text-zinc-400 mb-2" />
          <p className="text-zinc-700 font-semibold">No closed seasons yet.</p>
          <p className="text-zinc-500 mt-1 max-w-md mx-auto">
            Close a season above and Dugsy will draft payout rows here for each winner based on the pool &amp; prize-fund split.
          </p>
        </div>
      ) : (
        <>
          {/* Season picker — every CLOSED season, newest first */}
          {closures.length > 0 ? (
            <div className="mb-5 flex items-center gap-3 flex-wrap" data-testid="payouts-season-filter">
              <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Closed season</Label>
              <select
                value={selectedSeasonId}
                onChange={(e) => setSelectedSeasonId(e.target.value)}
                data-testid="payouts-season-select"
                className="h-10 rounded-sm border border-zinc-300 bg-white px-2 text-sm min-w-[260px]"
              >
                {closures.map((c) => {
                  const label = `${c.season_name || "Season"} · closed ${shortDate(c.closed_at)}`;
                  return (
                    <option key={c.id} value={c.season_id || ""} data-testid={`payouts-season-option-${c.season_id || c.id}`}>
                      {label}
                    </option>
                  );
                })}
              </select>
              {selectedClosure?.prize_pence ? (
                <span className="text-xs text-zinc-500">
                  Prize pot at close: <strong className="text-zinc-950 tabular-nums">{formatPounds(selectedClosure.prize_pence)}</strong>
                </span>
              ) : null}
            </div>
          ) : (
            <div className="mb-5 text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2" data-testid="payouts-no-closures-orphan">
              No closed seasons on record yet — but there are unpaid winners below from earlier payouts.
            </div>
          )}

          {/* Selected season block */}
          {closures.length === 0 ? null : !selectedSeasonId ? (
            <p className="text-sm text-zinc-500" data-testid="payouts-no-selection">Pick a closed season from the dropdown above.</p>
          ) : seasonPayouts.length === 0 ? (
            <div className="border border-amber-300 rounded-sm bg-amber-50 p-5" data-testid="payouts-needs-generate">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-base font-bold text-zinc-950">
                    No payouts drafted yet for {selectedClosure?.season_name || "this season"}.
                  </p>
                  <p className="text-sm text-zinc-700 mt-1">
                    This season was closed before the auto-payout feature shipped (or the draft was deleted).
                    Click below to draft rows using the saved final standings + your current
                    {" "}<strong>Pool &amp; prize fund</strong> split.
                  </p>
                  <Button
                    onClick={generateForSelected}
                    disabled={generating}
                    data-testid="payouts-generate-btn"
                    className="mt-4 h-10 rounded-sm bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-2"
                  >
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {generating ? "Drafting…" : "Draft payouts for this season"}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="payouts-season-group">
              <div className="bg-zinc-50 px-4 py-3 flex items-center justify-between flex-wrap gap-3 border-b border-zinc-200">
                <div className="flex items-baseline gap-3 flex-wrap min-w-0">
                  <h3 className="font-heading text-lg font-bold text-zinc-950 truncate" data-testid="payouts-season-name">
                    {selectedClosure?.season_name || "Season"}
                  </h3>
                  <span className="text-xs text-zinc-500">
                    {seasonPayouts.length} winner{seasonPayouts.length === 1 ? "" : "s"} · closed {shortDate(selectedClosure?.closed_at)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-amber-700">
                    Owed <strong className="tabular-nums">{formatPounds(seasonPayouts.filter((r) => !r.paid).reduce((s, r) => s + (r.amount_pence || 0), 0))}</strong>
                  </span>
                  <span className="text-emerald-700">
                    Paid <strong className="tabular-nums">{formatPounds(seasonPayouts.filter((r) => r.paid).reduce((s, r) => s + (r.amount_pence || 0), 0))}</strong>
                  </span>
                </div>
              </div>
              <ul className="divide-y divide-zinc-100">
                {seasonPayouts.map((r) => {
                  const draftedNote = notesDraft[r.id];
                  const noteValue = draftedNote !== undefined ? draftedNote : (r.notes || "");
                  const isDirty = draftedNote !== undefined && draftedNote.trim() !== (r.notes || "").trim();
                  const isVoucher = r.payment_method === "voucher";
                  const rankBadge = r.rank === 1 ? (
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-gradient-to-b from-amber-400 to-amber-600 text-white shrink-0" aria-label="Rank 1">
                      <Crown className="h-3.5 w-3.5" />
                    </span>
                  ) : r.rank ? (
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm border border-zinc-300 text-zinc-700 font-heading text-xs font-bold tabular-nums shrink-0" aria-label={`Rank ${r.rank}`}>
                      {r.rank}
                    </span>
                  ) : null;
                  if (isVoucher) {
                    // Compact voucher row — one line on desktop, two lines on
                    // mobile (identity + actions on row 1, code / expiry /
                    // note stacked onto row 2). Expired vouchers get a solid
                    // red pill so at-a-glance you can spot lapsed prizes.
                    const expiryDate = r.voucher_expires_at ? new Date(r.voucher_expires_at) : null;
                    const isExpired = expiryDate && !Number.isNaN(expiryDate.getTime()) && expiryDate.getTime() < Date.now();
                    return (
                      <li key={r.id} data-testid={`payout-row-${r.id}`} className="px-4 py-3">
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                          {rankBadge}
                          <p className="font-heading text-sm font-bold text-zinc-950 truncate" data-testid={`payout-winner-name-${r.id}`}>
                            {r.winner_name}
                          </p>
                          <p className="font-heading text-sm font-bold text-zinc-950 tabular-nums" data-testid={`payout-amount-${r.id}`}>
                            {r.voucher_prize_display || r.amount_pence ? formatPounds(r.amount_pence) : "Voucher"}
                          </p>
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] bg-amber-100 text-amber-900 border border-amber-300" data-testid={`payout-voucher-meta-${r.id}`}>
                            Voucher
                          </span>

                          {/* Middle group — wraps to a new line on mobile
                              (order-last + w-full), sits inline on desktop
                              (order-none + flex-1). */}
                          <div className="order-last w-full sm:order-none sm:w-auto sm:flex-1 sm:min-w-0 flex items-center gap-2 sm:gap-3 flex-wrap">
                            {r.voucher_code && (
                              <code className="font-mono text-xs font-bold tracking-wider text-zinc-950" data-testid={`payout-voucher-code-${r.id}`}>
                                {r.voucher_code}
                              </code>
                            )}
                            {r.voucher_expires_at && (
                              <span
                                className={
                                  isExpired
                                    ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase tracking-[0.14em] bg-rose-600 text-white border border-rose-700"
                                    : "text-[11px] text-rose-700 font-semibold"
                                }
                                data-testid={`payout-voucher-expiry-${r.id}`}
                                data-expired={isExpired ? "true" : "false"}
                              >
                                {isExpired ? "Expired" : "Expires"} {shortDate(r.voucher_expires_at)}
                              </span>
                            )}
                            <input
                              type="text"
                              value={noteValue}
                              onChange={(e) => onNotesChange(r.id, e.target.value)}
                              onBlur={() => isDirty && saveNote(r)}
                              onKeyDown={(e) => { if (e.key === "Enter" && isDirty) { e.preventDefault(); saveNote(r); } }}
                              data-testid={`payout-note-${r.id}`}
                              placeholder="Add note…"
                              className="flex-1 min-w-[140px] h-8 rounded-sm border border-zinc-200 bg-zinc-50 px-2 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 focus:bg-white"
                            />
                          </div>

                          {/* Actions — pushed to right of row 1 on mobile via
                              ml-auto, sit after middle group on desktop. */}
                          <div className="ml-auto flex items-center gap-3 shrink-0">
                            <div className="inline-flex items-center gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Paid</span>
                              <Switch
                                checked={!!r.paid}
                                onCheckedChange={() => togglePaid(r)}
                                data-testid={`payout-paid-switch-${r.id}`}
                                aria-label={`Mark ${r.winner_name} ${r.paid ? "unpaid" : "paid"}`}
                              />
                              {r.paid && <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeRow(r)}
                              data-testid={`payout-delete-${r.id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  }
                  return (
                    <li key={r.id} data-testid={`payout-row-${r.id}`} className="px-4 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {r.rank === 1 ? (
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-gradient-to-b from-amber-400 to-amber-600 text-white shrink-0" aria-label="Rank 1">
                                <Crown className="h-3.5 w-3.5" />
                              </span>
                            ) : r.rank ? (
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm border border-zinc-300 text-zinc-700 font-heading text-xs font-bold tabular-nums" aria-label={`Rank ${r.rank}`}>
                                {r.rank}
                              </span>
                            ) : null}
                            <p className="font-heading text-base font-bold text-zinc-950 truncate" data-testid={`payout-winner-name-${r.id}`}>
                              {r.winner_name}
                            </p>
                            {r.split_pct ? (
                              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                                {`${r.split_pct}% of prize`}
                              </span>
                            ) : null}
                          </div>
                          {r.winner_email && (
                            <p className="text-xs text-zinc-500 mt-0.5 truncate" data-testid={`payout-winner-email-${r.id}`}>
                              {r.winner_email}
                            </p>
                          )}
                          <p className="font-heading text-2xl font-bold text-zinc-950 mt-2 tabular-nums" data-testid={`payout-amount-${r.id}`}>
                            {formatPounds(r.amount_pence)}
                          </p>

                          <div className="mt-3">
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Note</Label>
                            <Textarea
                              value={noteValue}
                              onChange={(e) => onNotesChange(r.id, e.target.value)}
                              onBlur={() => isDirty && saveNote(r)}
                              data-testid={`payout-note-${r.id}`}
                              rows={2}
                              placeholder="e.g. paid via Revolut, ref #12345"
                              className="mt-1 rounded-sm border-zinc-300 text-sm"
                            />
                            {isDirty && (
                              <div className="mt-1 flex items-center gap-2">
                                <Button
                                  type="button"
                                  onClick={() => saveNote(r)}
                                  disabled={savingNoteId === r.id}
                                  data-testid={`payout-note-save-${r.id}`}
                                  className="h-8 px-3 rounded-sm bg-zinc-950 text-white text-xs hover:bg-zinc-800"
                                >
                                  {savingNoteId === r.id ? "Saving…" : "Save note"}
                                </Button>
                                <button
                                  type="button"
                                  onClick={() => setNotesDraft((s) => { const { [r.id]: _, ...rest } = s; return rest; })}
                                  className="text-xs font-semibold text-zinc-500 hover:text-zinc-950"
                                >
                                  Discard
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-3 shrink-0">
                          <div className="inline-flex items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Paid</span>
                            <Switch
                              checked={!!r.paid}
                              onCheckedChange={() => togglePaid(r)}
                              data-testid={`payout-paid-switch-${r.id}`}
                              aria-label={`Mark ${r.winner_name} ${r.paid ? "unpaid" : "paid"}`}
                            />
                            {r.paid && <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />}
                          </div>
                          {r.paid && r.paid_at && (
                            <p className="text-[10px] text-zinc-500 -mt-2">on {shortDate(r.paid_at)}</p>
                          )}
                          <button
                            type="button"
                            onClick={() => removeRow(r)}
                            data-testid={`payout-delete-${r.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Unpaid winners — across ALL seasons */}
          <div className="mt-10" data-testid="payouts-unpaid-section">
            <header className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-heading text-lg font-bold text-zinc-950">Unpaid winners</h3>
              <span className="text-xs text-zinc-500">
                Across every closed season · <strong className="text-amber-800 tabular-nums">{formatPounds(totalOwed)}</strong> outstanding
              </span>
            </header>
            {unpaidAll.length === 0 ? (
              <div className="text-sm py-6 text-center border border-dashed border-emerald-300 rounded-sm bg-emerald-50/50" data-testid="payouts-unpaid-empty">
                <CheckCircle2 className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
                <p className="text-emerald-900 font-semibold">All winners paid — nice one.</p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-zinc-200 rounded-sm">
                <table className="w-full text-sm min-w-[640px]" data-testid="payouts-unpaid-table">
                  <thead className="bg-zinc-50">
                    <tr className="text-left text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                      <th className="px-3 py-2">Winner</th>
                      <th className="px-3 py-2">Season</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {unpaidAll.map((r) => (
                      <tr key={r.id} data-testid={`payouts-unpaid-row-${r.id}`}>
                        <td className="px-3 py-3 align-top">
                          <p className="font-semibold text-zinc-950 truncate" data-testid={`payouts-unpaid-name-${r.id}`}>{r.winner_name}</p>
                          {r.winner_email && <p className="text-xs text-zinc-500 truncate">{r.winner_email}</p>}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <p className="text-zinc-800 truncate" data-testid={`payouts-unpaid-season-${r.id}`}>{r.season_name || "—"}</p>
                          {r.rank ? <p className="text-[10px] text-zinc-500">Rank #{r.rank}{r.split_pct ? ` · ${r.split_pct}%` : ""}</p> : null}
                        </td>
                        <td className="px-3 py-3 align-top text-right tabular-nums font-bold text-zinc-950" data-testid={`payouts-unpaid-amount-${r.id}`}>
                          {formatPounds(r.amount_pence)}
                        </td>
                        <td className="px-3 py-3 align-top text-right">
                          <button
                            type="button"
                            onClick={() => { setSelectedSeasonId(r.season_id || ""); }}
                            data-testid={`payouts-unpaid-jump-${r.id}`}
                            className="text-xs font-semibold text-[#0038FF] hover:underline"
                          >
                            Open season →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
